import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import './Navbar.css';
import quoteService from '../../services/quoteService';

import { useAuth } from '../../contexts/AuthContext';

const Navbar = React.memo(() => {
    const { t, i18n } = useTranslation();
    const { isAuthenticated, logout, user } = useAuth();
    const navigate = useNavigate();
    const [totalUnread, setTotalUnread] = useState(0);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [currentSubmenu, setCurrentSubmenu] = useState(null); // 'settings' or null

    const toggleMenu = () => {
        setIsMenuOpen(!isMenuOpen);
        setCurrentSubmenu(null); // Reset submenu when closing
    };

    const closeMenu = () => {
        setIsMenuOpen(false);
        setCurrentSubmenu(null);
    };

    const openSubmenu = (submenu) => {
        setCurrentSubmenu(submenu);
    };

    const backToMain = () => {
        setCurrentSubmenu(null);
    };

    const changeLanguage = (lng) => {
        i18n.changeLanguage(lng);
    };

    const handleLogout = () => {
        logout();
        closeMenu();
        navigate('/');
    };

    // Fetch unread count for navbar badge
    useEffect(() => {
        if (!isAuthenticated || !user?.profileId) {
            return;
        }

        const fetchUnreadCount = async () => {
            try {
                const response = await quoteService.getQuoteUpdates();
                const quotes = response.quotes || [];
                const total = quotes.reduce((sum, q) => sum + (q.unreadCount || 0), 0);
                setTotalUnread(total);
            } catch (error) {
                console.error('[Navbar] Error fetching unread count:', error);
            }
        };

        // Initial fetch
        fetchUnreadCount();

        // Poll every 30 seconds
        const interval = setInterval(fetchUnreadCount, 30000);

        return () => clearInterval(interval);
    }, [isAuthenticated, user?.profileId]);

    return (
        <>
            <nav className={`navbar ${isMenuOpen ? 'menu-open' : ''}`}>
                <div className="navbar-container">
                    <Link to={isAuthenticated ? "/home" : "/"} className="navbar-logo" onClick={closeMenu}>
                        <span style={{
                            fontFamily: "'Courier Prime', monospace",
                            fontWeight: '700',
                            color: 'var(--color-primary)',
                            fontSize: '1.5rem',
                            letterSpacing: '1px'
                        }}>NOVAINK</span>
                    </Link>

                    <div className={`navbar-actions ${isMenuOpen ? 'active' : ''}`}>
                        {currentSubmenu === 'settings' ? (
                            // Settings Submenu
                            <>
                                <button className="nav-link back-button" onClick={backToMain}>
                                    ← {t('back', 'VOLTAR')}
                                </button>
                                <div className="submenu-title">{t('settings', 'CONFIGURAÇÕES')}</div>
                                <Link to="/settings/profile" className="nav-link" onClick={closeMenu}>
                                    {t('edit_profile', 'EDITAR PERFIL')}
                                </Link>
                                <Link to="/settings/account" className="nav-link" onClick={closeMenu}>
                                    {t('account', 'CONTA')}
                                </Link>
                                <Link to="/settings/privacy" className="nav-link" onClick={closeMenu}>
                                    {t('privacy', 'PRIVACIDADE')}
                                </Link>
                                <Link to="/settings/notifications" className="nav-link" onClick={closeMenu}>
                                    {t('notifications', 'NOTIFICAÇÕES')}
                                </Link>
                            </>
                        ) : (
                            // Main Menu
                            <>
                                {isAuthenticated && (
                                    <Link
                                        to={user?.role === 'client' ? "/my-quotes" : "/artist/quotes"}
                                        className="nav-link nav-link-with-badge"
                                        onClick={closeMenu}
                                    >
                                        {user?.role === 'client'
                                            ? t('my_quote_requests', 'MINHAS SOLICITAÇÕES').toUpperCase()
                                            : t('quotes', 'SOLICITAÇÕES').toUpperCase()
                                        }
                                        {totalUnread > 0 && (
                                            <span className="navbar-notification-badge">{totalUnread}</span>
                                        )}
                                    </Link>
                                )}
                                {isAuthenticated ? (
                                    <>
                                        <Link to="/home" className="nav-link" onClick={closeMenu}>
                                            {user?.role === 'client' ? t('home', 'INÍCIO').toUpperCase() : t('dashboard', 'DASHBOARD').toUpperCase()}
                                        </Link>
                                        <button className="nav-link" onClick={() => openSubmenu('settings')}>
                                            {t('settings', 'CONFIGURAÇÕES').toUpperCase()} →
                                        </button>
                                        <button onClick={handleLogout} className="nav-link logout-btn-nav">
                                            {t('logout', 'SAIR').toUpperCase()}
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <Link to="/login" className="nav-link" onClick={closeMenu}>{t('login', 'ENTRAR').toUpperCase()}</Link>
                                    </>
                                )}

                                <div className="language-switcher">
                                    <button
                                        className={`lang-btn ${i18n.language === 'pt' ? 'active' : ''}`}
                                        onClick={() => { changeLanguage('pt'); }}
                                    >
                                        PT
                                    </button>
                                    <span className="separator">|</span>
                                    <button
                                        className={`lang-btn ${i18n.language.includes('en') ? 'active' : ''}`}
                                        onClick={() => { changeLanguage('en'); }}
                                    >
                                        EN
                                    </button>
                                </div>
                            </>
                        )}
                    </div>

                    <button className={`hamburger ${isMenuOpen ? 'active' : ''}`} onClick={toggleMenu} aria-label="Menu">
                        <span></span>
                        <span></span>
                        <span></span>
                    </button>
                </div>
            </nav>
        </>
    );
});

export default Navbar;
